import { merge } from "lodash";
import { FC, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import { copy } from "shared/assets";
import { addTemporaryToast } from "shared/components/Toast";
import { IAPIState, IFormButtons, IStatus } from "shared/types";

import { MemberForm } from "Bands/components";
import { useBandContext, usePermissionsContext } from "Bands/contexts";
import { BAND_PATHS } from "Bands/router";
import { clearActionMeta, dataMetaSelector, editBandUser } from "Bands/state";
import { BAND_ACTIONS, IBandInvitationProps } from "Bands/types";
import { useBandRoute, useClearActionMetaOnUnMount } from "Bands/utils";

const editMemberCopy = copy.members.editMember;

interface EditMemberFormProps {}

export const EditMemberForm: FC<EditMemberFormProps> = () => {
  const { userCanManageBand } = usePermissionsContext();

  const dispatch = useDispatch();
  const { pushRoute } = useBandRoute();

  let { userId } = useParams<{ userId: string }>();
  const { bandUsers } = useBandContext();

  const member = bandUsers.find(({ id }) => userId === id);

  const { api: apiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.editBandUser)
  );
  useClearActionMetaOnUnMount(BAND_ACTIONS.editBandUser);

  const handleEditSuccess = useCallback(() => {
    dispatch(
      addTemporaryToast({
        status: IStatus.success,
        message: editMemberCopy.messages.success,
      })
    );

    pushRoute(BAND_PATHS.MEMBERS);
  }, [dispatch, pushRoute]);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(clearActionMeta(BAND_ACTIONS.editBandUser));
      handleEditSuccess();
    }
  }, [apiMeta?.status, dispatch, handleEditSuccess]);

  const buttons: IFormButtons = useMemo(
    () =>
      ({
        primary: true,
      } as IFormButtons),
    []
  );

  const defaultUserValues = useMemo(
    () => ({
      email: member?.email,
      firstname: member?.firstname,
      lastname: member?.lastname,
    }),
    [member]
  );

  const defaultBandUserValues = useMemo(
    () => ({
      nickname: member?.nickname,
      function: member?.function,
      category: member?.category?.id,
    }),
    [member]
  );

  const defaultValues = useMemo(
    () => merge({}, defaultUserValues, defaultBandUserValues),
    [defaultUserValues, defaultBandUserValues]
  );

  const handleSubmit = (invitation: IBandInvitationProps) => {
    const { nickname, function: userFunction, category } = invitation;

    const bandUserValues = { nickname, function: userFunction, category };

    dispatch(
      editBandUser({
        bandUserId: userId,
        bandUserData: bandUserValues,
      })
    );
  };

  return (
    <MemberForm
      readOnlyEmail
      readOnlyUserName
      error={!!apiMeta?.error}
      apiStatus={apiMeta?.status}
      handleSubmit={handleSubmit}
      buttons={buttons}
      defaultValues={defaultValues}
      apiError={apiMeta?.error}
      canManageMemberCategories={userCanManageBand}
    />
  );
};
